import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useCustomerAuth, type CustomerUser } from '@/hooks/useCustomerAuth';


interface CustomerLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
  subtitle?: string;
}

type Step = 'input' | 'otp' | 'name' | 'success';

// Generate a 6-digit OTP locally (stored in sessionStorage for verification)
function generateLocalOTP(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

// Simulate OTP send (in real app, call an SMS/email API)
// We store OTP in sessionStorage with expiry
function sendOTPLocally(contact: string): string {
  const otp = generateLocalOTP();
  const expiry = Date.now() + 5 * 60 * 1000; // 5 min
  sessionStorage.setItem(`otp_${contact}`, JSON.stringify({ otp, expiry }));
  // In development, log to console
  console.log(`[ClothyBazar OTP] ${contact} → ${otp}`);
  return otp;
}

function verifyLocalOTP(contact: string, entered: string): boolean {
  try {
    const raw = sessionStorage.getItem(`otp_${contact}`);
    if (!raw) return false;
    const { otp, expiry } = JSON.parse(raw);
    if (Date.now() > expiry) return false;
    return otp === entered;
  } catch {
    return false;
  }
}

export default function CustomerLoginModal({
  isOpen,
  onClose,
  onSuccess,
  title = 'Login to Continue',
  subtitle = 'Enter your phone or email to get an OTP',
}: CustomerLoginModalProps) {
  const { login } = useCustomerAuth();

  const [step, setStep] = useState<Step>('input');
  const [contact, setContact] = useState('');
  const [contactType, setContactType] = useState<'phone' | 'email'>('phone');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [name, setName] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [displayOTP, setDisplayOTP] = useState(''); // show OTP on screen for demo
  const [showSignupBonus, setShowSignupBonus] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Detect contact type
  useEffect(() => {
    if (contact.includes('@')) setContactType('email');
    else setContactType('phone');
  }, [contact]);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep('input');
      setContact('');
      setOtpDigits(['', '', '', '', '', '']);
      setName('');
      setError('');
      setDisplayOTP('');
    }
  }, [isOpen]);

  // Countdown timer
  const startResendTimer = useCallback(() => {
    setResendTimer(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  // Send OTP
  const handleSendOTP = async () => {
    const trimmed = contact.trim();
    if (!trimmed) { setError('Phone ya email daalo'); return; }
    if (contactType === 'phone' && !/^\+?[0-9]{10,15}$/.test(trimmed.replace(/\s/g, ''))) {
      setError('Valid phone number daalo (10 digits)');
      return;
    }
    if (contactType === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Valid email address daalo');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Check if user exists in Supabase
      const { data: existing, error: fetchError } = await supabase
        .from('customers')
        .select('phone, name, email')
        .eq(contactType === 'phone' ? 'phone' : 'email', trimmed)
        .maybeSingle();

      if (fetchError) {
        console.error('Supabase fetch error:', fetchError);
        setError('Database se connect nahi ho paaya. Dobara try karo.');
        setLoading(false);
        return;
      }

      const newUser = !existing;
      setIsNewUser(newUser);
      if (existing?.name) {
        setName(existing.name);
      } else {
        setName(''); // Reset name for new user
      }

      // Send OTP
      const otp = sendOTPLocally(trimmed);
      setDisplayOTP(otp); // show for demo purposes
      startResendTimer();
      setStep('otp');
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      console.error('Send OTP error:', err);
      setError('Kuch error aa gaya. Dobara try karo.');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input
  const handleOtpChange = (idx: number, val: string) => {
    if (!/^\d*$/.test(val)) return;
    const newDigits = [...otpDigits];
    newDigits[idx] = val.slice(-1);
    setOtpDigits(newDigits);
    if (val && idx < 5) {
      otpRefs.current[idx + 1]?.focus();
    }
    setError('');
  };

  const handleOtpKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtpDigits(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  // Verify OTP
  const handleVerifyOTP = async () => {
    const entered = otpDigits.join('');
    if (entered.length !== 6) { setError('6-digit OTP daalo'); return; }

    const valid = verifyLocalOTP(contact.trim(), entered);
    if (!valid) {
      setError('Galat OTP hai ya expire ho gaya. Dobara try karo.');
      return;
    }

    sessionStorage.removeItem(`otp_${contact.trim()}`);

    if (isNewUser) {
      setStep('name');
    } else {
      // Existing user — log in directly
      await finalizeLogin(name);
    }
  };

  // Finalize login / registration
  const finalizeLogin = async (customerName: string) => {
    setLoading(true);
    const trimmed = contact.trim();

    try {
      if (isNewUser) {
        // Insert new customer
        const { error: insertError } = await supabase.from('customers').insert([{
          phone: contactType === 'phone' ? trimmed : `email_${trimmed}`,
          email: contactType === 'email' ? trimmed : null,
          name: customerName,
          registered_at: new Date().toISOString(),
          last_login_at: new Date().toISOString(),
        }]);
        if (insertError) {
          console.error('Insert error:', insertError);
          setError('Account banane mein problem. Dobara try karo.');
          setLoading(false);
          return;
        }
      } else {
        // Update last login
        const { error: updateError } = await supabase
          .from('customers')
          .update({ last_login_at: new Date().toISOString() })
          .eq(contactType === 'phone' ? 'phone' : 'email', trimmed);
        if (updateError) {
          console.error('Update error:', updateError);
          // Don't block login if update fails, just log it
        }
      }

      // Give signup bonus to new users (₹50)
      if (isNewUser) {
        try {
          const { data: newCust } = await supabase
            .from('customers')
            .select('id,wallet_balance,wallet_transactions,signup_bonus_given')
            .eq(contactType === 'phone' ? 'phone' : 'email', trimmed)
            .maybeSingle();
          if (newCust && !newCust.signup_bonus_given) {
            const newBalance = (newCust.wallet_balance ?? 0) + 50;
            const newTx = [...(newCust.wallet_transactions ?? []), {
              type: 'bonus', amount: 50,
              description: 'Signup Bonus - Welcome to ClothyBazar!',
              timestamp: new Date().toISOString(),
            }];
            await supabase.from('customers').update({
              wallet_balance: newBalance,
              wallet_transactions: newTx,
              signup_bonus_given: true,
            }).eq('id', newCust.id);
            // Play bonus sound
            try {
              const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
              const playTone = (freq: number, start: number, dur: number) => {
                const osc = ctx.createOscillator(); const gain = ctx.createGain();
                osc.connect(gain); gain.connect(ctx.destination);
                osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
                gain.gain.setValueAtTime(0.3, ctx.currentTime + start);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
                osc.start(ctx.currentTime + start); osc.stop(ctx.currentTime + start + dur + 0.05);
              };
              playTone(523, 0, 0.12); playTone(659, 0.13, 0.12); playTone(784, 0.26, 0.12); playTone(1047, 0.39, 0.4);
            } catch { /* ignore */ }
            setShowSignupBonus(true);
          }
        } catch { /* ignore bonus errors */ }
      }

      // Save to localStorage auth
      const userData: CustomerUser = {
        name: customerName,
        phone: contactType === 'phone' ? trimmed : '',
        email: contactType === 'email' ? trimmed : undefined,
        registeredAt: new Date().toISOString(),
      };

      // Sync address if exists
      const savedAddr = localStorage.getItem('userAddress');
      if (savedAddr) {
        try { userData.address = JSON.parse(savedAddr); } catch { /* ignore */ }
      }

      // If no address saved yet, pre-fill name + phone for easier ordering
      if (!savedAddr) {
        localStorage.setItem('userAddress', JSON.stringify({
          fullName: customerName,
          phone: contactType === 'phone' ? trimmed : '',
          address: '',
          city: 'Dehradun',
          pincode: '',
        }));
      } else {
        // Update fullName and phone in existing address if empty
        try {
          const existingAddr = JSON.parse(savedAddr);
          const needsUpdate = !existingAddr.fullName || !existingAddr.phone;
          if (needsUpdate) {
            existingAddr.fullName = existingAddr.fullName || customerName;
            existingAddr.phone = existingAddr.phone || (contactType === 'phone' ? trimmed : '');
            localStorage.setItem('userAddress', JSON.stringify(existingAddr));
          }
        } catch { /* ignore */ }
      }

      // Call login function to update auth state
      login(userData);
      
      setStep('success');
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, isNewUser ? 3500 : 1200);
    } catch (err) {
      console.error('Finalize login error:', err);
      setError('Login mein problem. Dobara try karo.');
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Apna naam daalo'); return; }
    finalizeLogin(name.trim());
  };

  const handleResend = () => {
    if (resendTimer > 0) return;
    const otp = sendOTPLocally(contact.trim());
    setDisplayOTP(otp);
    startResendTimer();
    setOtpDigits(['', '', '', '', '', '']);
    setError('');
    setTimeout(() => otpRefs.current[0]?.focus(), 100);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden animate-slide-in">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 px-6 pt-8 pb-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/70 hover:bg-white/20 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-orange-400 to-red-500 flex items-center justify-center mb-4">
            <i className="ri-t-shirt-line text-white text-2xl"></i>
          </div>
          <h2 className="text-white font-bold text-xl mb-1">
            {step === 'success' ? 'Welcome!' : step === 'name' ? 'Almost Done!' : step === 'otp' ? 'Enter OTP' : 'Login'}
          </h2>
        </div>

        <div className="px-6 py-6">
          {/* Step: Input phone/email */}
          {step === 'input' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number ya Email
              </label>
              <div className="relative mb-4">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className={`text-gray-400 ${contact.includes('@') ? 'ri-mail-line' : 'ri-phone-line'}`}></i>
                </div>
                <input
                  type="text"
                  value={contact}
                  onChange={e => { setContact(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleSendOTP()}
                  placeholder="9876543210 ya email@gmail.com"
                  className="w-full pl-11 pr-4 py-3.5 rounded-2xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20 outline-none text-sm text-gray-900 bg-gray-50"
                  autoFocus
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-500 text-xs mb-4 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  <i className="ri-error-warning-line shrink-0"></i>
                  {error}
                </div>
              )}

              <button
                onClick={handleSendOTP}
                disabled={loading || !contact.trim()}
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-gray-900 to-gray-700 text-white font-bold text-sm hover:from-gray-800 hover:to-gray-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Checking...</>
                ) : (
                  <><i className="ri-send-plane-fill"></i>Continue</>
                )}
              </button>

              <p className="text-center text-gray-400 text-xs mt-4">
                {isNewUser ? 'New user? Account banega automatically' : 'Already registered? Login ho jayega'}
              </p>
            </div>
          )}

          {/* Step: OTP */}
          {step === 'otp' && (
            <div>
              <p className="text-gray-600 text-sm mb-4 text-center">
                <span className="font-semibold text-gray-900">{contact}</span> pe OTP bheja gaya
              </p>

              {/* OTP Demo Banner */}
              {displayOTP && (
                <div className="mb-4 p-3 rounded-2xl bg-amber-50 border-2 border-amber-300 text-center">
                  <p className="text-amber-600 text-xs font-semibold mb-1">Demo Mode — Aapka OTP:</p>
                  <p className="text-amber-900 font-mono font-bold text-2xl tracking-[0.3em]">{displayOTP}</p>
                  <p className="text-amber-500 text-[10px] mt-1">Real app mein SMS/Email pe aata</p>
                </div>
              )}

              {/* OTP Input Boxes */}
              <div className="flex gap-2 justify-center mb-4" onPaste={handleOtpPaste}>
                {otpDigits.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={el => { otpRefs.current[idx] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOtpChange(idx, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(idx, e)}
                    className={`w-11 h-12 text-center text-xl font-bold rounded-xl border-2 outline-none transition-all ${
                      digit
                        ? 'border-orange-400 bg-orange-50 text-gray-900'
                        : 'border-gray-200 bg-gray-50 text-gray-900 focus:border-orange-400'
                    }`}
                  />
                ))}
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-500 text-xs mb-4 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  <i className="ri-error-warning-line shrink-0"></i>
                  {error}
                </div>
              )}

              <button
                onClick={handleVerifyOTP}
                disabled={otpDigits.join('').length !== 6}
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-gray-900 to-gray-700 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2 mb-3"
              >
                <i className="ri-shield-check-fill"></i>
                OTP Verify Karo
              </button>

              <div className="flex items-center justify-between text-xs text-gray-500">
                <button
                  onClick={() => { setStep('input'); setOtpDigits(['', '', '', '', '', '']); setError(''); }}
                  className="hover:text-gray-900 cursor-pointer transition-colors"
                >
                  ← Number badlo
                </button>
                <button
                  onClick={handleResend}
                  disabled={resendTimer > 0}
                  className={`font-semibold transition-colors cursor-pointer ${resendTimer > 0 ? 'text-gray-300' : 'text-orange-500 hover:text-orange-600'}`}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'OTP Resend karo'}
                </button>
              </div>
            </div>
          )}

          {/* Step: Enter Name */}
          {step === 'name' && (
            <form onSubmit={handleNameSubmit}>
              <p className="text-gray-500 text-sm mb-4 text-center">
                hello fill your name please&nbsp;
              </p>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                your name&nbsp;
              </label>
              <input
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); setError(''); }}
                placeholder="Apna poora naam likho"
                className="w-full px-4 py-3.5 rounded-2xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-400/20 outline-none text-sm text-gray-900 bg-gray-50 mb-4"
                autoFocus
              />

              {error && (
                <div className="flex items-center gap-2 text-red-500 text-xs mb-4 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                  <i className="ri-error-warning-line shrink-0"></i>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !name.trim()}
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-gray-900 to-gray-700 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Register ho raha hai...</>
                ) : (
                  <><i className="ri-user-add-fill"></i>Account Banao</>
                )}
              </button>
            </form>
          )}

          {/* Signup Bonus Banner */}
          {showSignupBonus && step === 'success' && (
            <div className="mb-4 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 p-4 text-center shadow-lg">
              <div className="text-3xl mb-1">🎁</div>
              <p className="text-white font-bold text-lg">₹50 Signup Bonus Mila!</p>
              <p className="text-white/80 text-xs mt-0.5">Wallet mein add ho gaya — pehle order pe use karo!</p>
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
              </div>
              <p className="text-gray-900 font-bold text-lg mb-1 capitalize">
                {isNewUser ? 'Welcome to ClothyBazar!' : `Welcome ${name}`}
              </p>
              <p className="text-gray-500 text-sm">Login successful!</p>
              <div className="mt-3 flex items-center justify-center gap-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
